import { useEffect, useMemo, useState } from 'react';
import { formatDateTime, formatMoney, istanbulDayString, t } from '@aquabox/shared';
import { useExternalLedgerPreview, usePostExternalLedger } from '../api/queries';
import type {
  ExternalLedgerPostOutcome,
  ExternalLedgerPreview,
  ExternalLedgerPreviewRow,
  LedgerHoldCode,
  LedgerPolicy,
  LedgerRowState,
} from '../api/types';

const HOLD_REASON_KEYS: Record<LedgerHoldCode, string> = {
  NOT_DELIVERED: 'extLedgerUi.holdReason.NOT_DELIVERED',
  NO_POLICY: 'extLedgerUi.holdReason.NO_POLICY',
  ITEM_MISMATCH: 'extLedgerUi.holdReason.ITEM_MISMATCH',
  MIXED_JUGS: 'extLedgerUi.holdReason.MIXED_JUGS',
  NEEDS_CUSTOMER: 'extLedgerUi.holdReason.NEEDS_CUSTOMER',
  PAYMENT_UNSUPPORTED: 'extLedgerUi.holdReason.PAYMENT_UNSUPPORTED',
  AMOUNT_UNREAD: 'extLedgerUi.holdReason.AMOUNT_UNREAD',
  DEPOSIT_CONFLICT: 'extLedgerUi.holdReason.DEPOSIT_CONFLICT',
  TRIP_DAY: 'extLedgerUi.holdReason.TRIP_DAY',
  CUSTOMER_ANONYMIZED: 'extLedgerUi.holdReason.CUSTOMER_ANONYMIZED',
  AMOUNT_MISMATCH: 'extLedgerUi.holdReason.AMOUNT_MISMATCH',
};

const STATE_COLOR: Record<LedgerRowState, string> = {
  READY: 'var(--primary-bg)',
  HOLD: 'rgba(232,163,23,.15)',
  POSTED: 'rgba(30,158,106,.15)',
  REVERSED: 'var(--surface)',
  NEEDS_REVERSAL: 'rgba(232,88,90,.15)',
};

/** POST sonuç özeti: her `outcome` değeri ayrı sayılır (hakem U-c). */
type PostTally = {
  posted: number;
  already: number;
  hold: number;
  conflict: number;
  notFound: number;
};

/** Kullanıcı kararı: 22.09.2026'dan bu yana kapanan siparişler dahil (ADR 2026-09-30). */
export const EXTERNAL_LEDGER_DEFAULT_FROM = '2026-09-22';

const STATE_TEXT_COLOR: Record<LedgerRowState, string> = {
  READY: 'var(--primary)',
  HOLD: 'var(--warning)',
  POSTED: 'var(--success)',
  REVERSED: 'var(--text-secondary)',
  NEEDS_REVERSAL: 'var(--danger)',
};

/**
 * Panelde kapanan siparişleri deftere işleme kartı.
 *
 * Yalnız OWNER görür; sunucu da 403 ile yabancı rolleri reddeder. Mantık basit:
 * tarih aralığı → önizleme → READY satırlar seçili → POST. Para ve eksi bakiye
 * uyarısı sunucudan gelir; panel hesap yapmaz.
 */
const EMPTY_ROWS: ExternalLedgerPreviewRow[] = [];

export function ExternalLedgerCard() {
  const today = istanbulDayString(new Date());
  const [from, setFrom] = useState(EXTERNAL_LEDGER_DEFAULT_FROM);
  const [to, setTo] = useState(today);
  const [active, setActive] = useState<{ from: string; to: string } | null>(null);

  const previewQ = useExternalLedgerPreview(
    active?.from ?? null,
    active?.to ?? null,
    Boolean(active),
  );
  const data = previewQ.data;
  // Sabit referans (`EMPTY_ROWS`) şart: `data?.rows ?? []` her render'da YENİ bir dizi üretir,
  // bu da aşağıdaki `readyIds`/`useEffect` zincirini her render'da tetikleyip sonsuz render
  // döngüsüne (`setSelected` → re-render → yeni `[]` → efekt tekrar) yol açıyordu.
  const rows = data?.rows ?? EMPTY_ROWS;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acceptNegative, setAcceptNegative] = useState(false);
  const [result, setResult] = useState<PostTally | null>(null);

  const readyIds = useMemo(
    () => rows.filter((r) => r.state === 'READY').map((r) => r.orderId),
    [rows],
  );

  // readyIds değişince seçimi READY kümesine hizala: önceki seferden kalan seçimleri sil,
  // yeni READY'leri varsayılan olarak seç. `useEffect` şart — render sırasında koşulsuz
  // `setState` "too many re-renders" üretiyordu (her render yeni `Set` referansı).
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set<string>();
      for (const id of readyIds) {
        if (prev.has(id)) next.add(id);
      }
      if (next.size === 0 && readyIds.length > 0) {
        for (const id of readyIds) next.add(id);
      }
      // İçerik aynıysa ÖNCEKİ referansı döndür — aksi halde her efekt çalışışında yeni bir
      // `Set` nesnesi state'e yazılır ve React'i sonsuz render döngüsüne sokar.
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [readyIds]);

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll(): void {
    setSelected(new Set(readyIds));
  }
  function deselectAll(): void {
    setSelected(new Set());
  }

  const totals = data?.totals;
  const hasRows = rows.length > 0;
  const selectedCount = selected.size;
  const allReadySelected = readyIds.length > 0 && readyIds.every((id) => selected.has(id));

  return (
    <div
      data-testid="external-ledger-card"
      className="card card-pad"
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 className="section-title">{t('extLedgerUi.title')}</h2>
      </div>
      <p className="muted-line" style={{ margin: 0 }}>
        {t('extLedgerUi.hint')}
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('extLedgerUi.from')}
          <input
            className="input"
            type="date"
            data-testid="ledger-from"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            style={{ height: 38 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          {t('extLedgerUi.to')}
          <input
            className="input"
            type="date"
            data-testid="ledger-to"
            value={to}
            min={from}
            max={today}
            onChange={(e) => setTo(e.target.value)}
            style={{ height: 38 }}
          />
        </label>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn-primary"
          data-testid="ledger-preview"
          onClick={() => {
            setResult(null);
            setActive({ from, to });
          }}
        >
          {t('extLedgerUi.preview')}
        </button>
      </div>

      {result ? (
        <div
          data-testid="ledger-result"
          style={{
            padding: '10px 14px',
            borderRadius: 'var(--r-row)',
            background: 'rgba(30,158,106,.12)',
            color: 'var(--success)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {t('extLedgerUi.postResult', result)}
          {result.conflict > 0 || result.notFound > 0 ? (
            <div
              data-testid="ledger-result-issues"
              style={{ color: 'var(--danger)', marginTop: 4 }}
            >
              {issueParts(result).join(' · ')}
            </div>
          ) : null}
        </div>
      ) : null}

      {previewQ.isError ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>
          {(previewQ.error as { message?: string } | null)?.message ?? t('error.internal')}
        </p>
      ) : null}

      {totals ? (
        <div
          data-testid="ledger-summary"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: 13,
            color: 'var(--text-secondary)',
            paddingTop: 4,
          }}
        >
          <strong style={{ fontSize: 13, color: 'var(--text-primary)' }}>
            {t('extLedgerUi.summary', {
              ready: totals.ready,
              hold: totals.hold,
              posted: totals.posted,
            })}
            {totals.needsReversal > 0
              ? t('extLedgerUi.summaryNeedReversal', { k: totals.needsReversal })
              : ''}
            {totals.reversed > 0 ? t('extLedgerUi.summaryReversed', { k: totals.reversed }) : ''}
          </strong>
          {totals.depotFullOut.length > 0 ? (
            <span data-testid="ledger-summary-full-out">
              {t('extLedgerUi.depotFullOut', { list: productsLine(totals.depotFullOut) })}
            </span>
          ) : null}
          {totals.depotEmptyIn.length > 0 ? (
            <span data-testid="ledger-summary-empty-in">
              {t('extLedgerUi.depotEmptyIn', { list: productsLine(totals.depotEmptyIn) })}
            </span>
          ) : null}
          <span>{t('extLedgerUi.cash', { amount: formatMoney(totals.cash) })}</span>
          {totals.depositUnits > 0 ? (
            <span>{t('extLedgerUi.depositUnits', { n: totals.depositUnits })}</span>
          ) : null}
          {totals.foreignUnits > 0 ? (
            <span>{t('extLedgerUi.foreignUnits', { n: totals.foreignUnits })}</span>
          ) : null}
          {totals.projectedDepotFull.length > 0 ? (
            <span data-testid="ledger-summary-projection">
              {t('extLedgerUi.projection', { list: productsLine(totals.projectedDepotFull) })}
            </span>
          ) : null}
        </div>
      ) : null}

      {hasRows ? (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="btn"
              data-testid="ledger-select-toggle"
              onClick={allReadySelected ? deselectAll : selectAll}
            >
              {allReadySelected ? t('extLedgerUi.deselectAll') : t('extLedgerUi.selectAll')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="ledger-post"
              disabled={selectedCount === 0}
              onClick={() => {
                setAcceptNegative(false);
                setConfirmOpen(true);
              }}
            >
              {t('extLedgerUi.postSelected', { n: selectedCount })}
            </button>
          </div>
          <LedgerTable rows={rows} selected={selected} onToggle={toggle} onlyReadyCanSelect />
        </>
      ) : active ? (
        !previewQ.isLoading ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
            {t('extLedgerUi.noRows')}
          </p>
        ) : null
      ) : null}

      {confirmOpen && data ? (
        <PostConfirmModal
          data={data}
          orderIds={Array.from(selected)}
          totals={data.totals}
          acceptNegative={acceptNegative}
          setAcceptNegative={setAcceptNegative}
          onCancel={() => setConfirmOpen(false)}
          onPosted={(r) => {
            setConfirmOpen(false);
            setResult(r);
            setSelected(new Set());
            void previewQ.refetch();
          }}
        />
      ) : null}
    </div>
  );
}

function LedgerTable({
  rows,
  selected,
  onToggle,
  onlyReadyCanSelect,
}: {
  rows: ExternalLedgerPreviewRow[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onlyReadyCanSelect: boolean;
}) {
  return (
    <div
      data-testid="ledger-table"
      className="card"
      style={{ overflow: 'auto', padding: 0, background: 'var(--surface)' }}
    >
      <div
        className="ledger-grid"
        style={{
          padding: '0 16px',
          height: 40,
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
        }}
      >
        <span>{t('extLedgerUi.colSelect')}</span>
        <span>{t('extLedgerUi.colTime')}</span>
        <span>{t('extLedgerUi.colSource')}</span>
        <span>{t('extLedgerUi.colCustomer')}</span>
        <span>{t('extLedgerUi.colItems')}</span>
        <span style={{ textAlign: 'right' }}>{t('extLedgerUi.colAmount')}</span>
        <span>{t('extLedgerUi.colState')}</span>
      </div>
      {rows.map((row) => {
        const canSelect = onlyReadyCanSelect ? row.state === 'READY' : true;
        const checked = selected.has(row.orderId);
        return (
          <div
            key={row.orderId}
            data-testid={`ledger-row-${row.orderId}`}
            className="ledger-grid"
            style={{
              padding: '8px 16px',
              borderTop: '1px solid var(--border)',
              fontSize: 13,
            }}
          >
            <span>
              {canSelect ? (
                <input
                  type="checkbox"
                  aria-label={t('extLedgerUi.colSelect')}
                  data-testid={`ledger-check-${row.orderId}`}
                  checked={checked}
                  onChange={() => onToggle(row.orderId)}
                />
              ) : null}
            </span>
            <span style={{ fontSize: 12.5 }}>{formatDateTime(row.receivedAt)}</span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                borderRadius: 4,
                background: row.source === 'HAYAT_PANEL' ? 'var(--ch-hayat)' : 'var(--success)',
                color: row.source === 'HAYAT_PANEL' ? '#fff' : 'var(--navy-900)',
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              {row.source === 'HAYAT_PANEL' ? 'H' : 'A'}
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.customerName ?? '—'}
              {row.externalStatusRaw ? (
                <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                  {' · '}
                  {row.externalStatusRaw}
                </span>
              ) : null}
            </span>
            <span style={{ fontSize: 12.5 }}>{itemsLine(row)}</span>
            <span style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(row.amount)}</span>
            <span>
              <span
                data-testid={`ledger-state-${row.orderId}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 8px',
                  borderRadius: 999,
                  background: STATE_COLOR[row.state],
                  color: STATE_TEXT_COLOR[row.state],
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                {t(stateKey(row.state) as Parameters<typeof t>[0])}
                {row.state === 'HOLD' && row.holdCode ? (
                  <span style={{ fontWeight: 500 }}>
                    · {t(HOLD_REASON_KEYS[row.holdCode] as Parameters<typeof t>[0])}
                  </span>
                ) : null}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function stateKey(state: LedgerRowState): string {
  switch (state) {
    case 'READY':
      return 'extLedgerUi.stateReady';
    case 'HOLD':
      return 'extLedgerUi.stateHold';
    case 'POSTED':
      return 'extLedgerUi.statePosted';
    case 'REVERSED':
      return 'extLedgerUi.stateReversed';
    case 'NEEDS_REVERSAL':
      return 'extLedgerUi.stateNeedsReversal';
    default:
      return 'extLedgerUi.stateReady';
  }
}

function itemsLine(row: ExternalLedgerPreviewRow): string {
  return row.items.map((it) => `${it.qty}× ${it.productName}`).join(', ');
}

function productsLine(items: { productId: string; name: string; qty: number }[]): string {
  return items.map((it) => `${it.name} × ${it.qty}`).join(' · ');
}

function PostConfirmModal({
  orderIds,
  totals,
  acceptNegative,
  setAcceptNegative,
  onCancel,
  onPosted,
}: {
  data: ExternalLedgerPreview;
  orderIds: string[];
  totals: ExternalLedgerPreview['totals'];
  acceptNegative: boolean;
  setAcceptNegative: (v: boolean) => void;
  onCancel: () => void;
  onPosted: (r: PostTally) => void;
}) {
  const post = usePostExternalLedger();
  const negative = totals.negative;

  // Sıralı 200'lük parçalar halinde gönder; sunucu kotası 1..200.
  async function run(): Promise<void> {
    try {
      const acc: PostTally = { posted: 0, already: 0, hold: 0, conflict: 0, notFound: 0 };
      for (let i = 0; i < orderIds.length; i += 200) {
        const slice = orderIds.slice(i, i + 200);
        const r = await post.mutateAsync({
          orderIds: slice,
          acceptNegative: negative ? acceptNegative : false,
        });
        for (const o of r.results) tally(o, acc);
      }
      onPosted(acc);
    } catch {
      // Hata kırmızı olarak modal'da görünür; kapanmaz, kullanıcı iptal edebilir.
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="ledger-confirm"
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !post.isPending) onCancel();
      }}
    >
      <div
        className="card modal-card"
        style={{ '--modal-w': '480px' } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <strong style={{ fontSize: 16 }}>{t('extLedgerUi.postConfirm')}</strong>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
            {t('extLedgerUi.summary', {
              ready: totals.ready,
              hold: totals.hold,
              posted: totals.posted,
            })}
          </p>
          {totals.depotFullOut.length > 0 ? (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {t('extLedgerUi.depotFullOut', { list: productsLine(totals.depotFullOut) })}
            </p>
          ) : null}
          {totals.depotEmptyIn.length > 0 ? (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {t('extLedgerUi.depotEmptyIn', { list: productsLine(totals.depotEmptyIn) })}
            </p>
          ) : null}
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {t('extLedgerUi.cash', { amount: formatMoney(totals.cash) })}
          </p>
          {negative ? (
            <div
              data-testid="ledger-negative-warning"
              style={{
                margin: 0,
                padding: 10,
                borderRadius: 6,
                background: 'var(--bg-danger-soft)',
                border: '1px solid var(--danger)',
                fontSize: 12.5,
                color: 'var(--danger)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <strong>{t('extLedgerUi.negativeWarning')}</strong>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  data-testid="ledger-accept-negative"
                  checked={acceptNegative}
                  onChange={(e) => setAcceptNegative(e.target.checked)}
                />
                {t('extLedgerUi.negativeAck')}
              </label>
            </div>
          ) : null}
          {post.isError ? (
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: 'var(--danger)',
              }}
            >
              {t('extLedgerUi.postFailed', {
                message:
                  (post.error as { message?: string } | null)?.message ?? t('error.internal'),
              })}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn"
              data-testid="ledger-confirm-cancel"
              onClick={onCancel}
              disabled={post.isPending}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="ledger-confirm-submit"
              disabled={post.isPending || (negative && !acceptNegative)}
              onClick={() => void run()}
            >
              {t('extLedgerUi.postSelected', { n: orderIds.length })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Sonuç satırının sorun parçaları: sıfır olan parça basılmaz ("0 sipariş bulunamadı" yok). */
function issueParts(r: PostTally): string[] {
  const parts: string[] = [];
  if (r.conflict > 0) parts.push(t('extLedgerUi.postResultConflict', { n: r.conflict }));
  if (r.notFound > 0) parts.push(t('extLedgerUi.postResultNotFound', { n: r.notFound }));
  return parts;
}

function tally(o: ExternalLedgerPostOutcome, acc: PostTally): void {
  if (o.outcome === 'POSTED') acc.posted += 1;
  else if (o.outcome === 'ALREADY_POSTED') acc.already += 1;
  else if (o.outcome === 'HOLD') acc.hold += 1;
  // Hakem U-c: CONFLICT (eşzamanlı işlem) ve NOT_FOUND ÖNCEDEN hiç sayılmıyordu — sonuç metni
  // 'işlendi' toplamı gönderilenden az çıkıyor, patron nedenini göremiyordu.
  else if (o.outcome === 'CONFLICT') acc.conflict += 1;
  else if (o.outcome === 'NOT_FOUND') acc.notFound += 1;
}

export { HOLD_REASON_KEYS };
export type { LedgerHoldCode, LedgerPolicy };
